import { jsx } from 'react/jsx-runtime';
import { createContext, useReducer, useEffect, useContext } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { LogLevel, PublicClientApplication } from '@azure/msal-browser';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var initialState = {
    isLoading: false,
    isLoadingRefreshToken: false,
    isAuthenticated: false,
    authenticationResult: null,
    redirect: "",
};
function reducer(state, action) {
    switch (action.type) {
        case "INIT_REDIRECT":
            return __assign(__assign({}, state), { isLoading: true });
        case "END_REDIRECT":
            return __assign(__assign({}, state), { isLoading: false, authenticationResult: action.payload, isAuthenticated: !!action.payload });
        case "INIT_REFRESHTOKEN":
            return __assign(__assign({}, state), { isLoadingRefreshToken: true });
        case "END_REFRESHTOKEN":
            return __assign(__assign({}, state), { isLoadingRefreshToken: false, authenticationResult: action.payload, isAuthenticated: !!action.payload });
        case "INIT_REDIRECT_PAGE_DEFAULT":
            return __assign(__assign({}, state), { redirect: action.payload.redirectDefault || "" });
        case "SET_REDIRECT_PAGE_ERROR":
            return __assign(__assign({}, state), { isAuthenticated: false, redirect: action.payload.redirectError || "" });
        default:
            throw new Error();
    }
}

var AZURE_AUTHENTICATION_RESULT = "@azure/authentication-result";

var AzureContext = createContext({
    msalInstance: null,
    authenticationResult: null,
    isLoading: false,
    isLoadingRefreshToken: false,
    refreshToken: function (scopesParam, methodHelper, isSetStorage) {
        methodHelper("accesstoken");
        console.log("not implemented", scopesParam, isSetStorage);
    },
    logout: function () {
        console.log("not implemented");
    },
    login: function (scopesParam) {
        console.log("not implemented", scopesParam);
    },
    isAuthenticated: false,
    redirect: "",
});

var StorageType;
(function (StorageType) {
    StorageType["sessionStorage"] = "sessionStorage";
    StorageType["localStorage"] = "localStorage";
})(StorageType || (StorageType = {}));

var Storage = /** @class */ (function () {
    function Storage(type) {
        var _this = this;
        this.type = type;
        this.setItem = function (key, value) {
            if (_this.type === StorageType.sessionStorage)
                sessionStorage.setItem(key, value);
            if (_this.type === StorageType.localStorage)
                localStorage.setItem(key, value);
        };
        this.getItem = function (key) {
            if (_this.type === StorageType.sessionStorage)
                return sessionStorage.getItem(key);
            if (_this.type === StorageType.localStorage)
                return localStorage.getItem(key);
            return null;
        };
        this.removeItem = function (key) {
            if (_this.type === StorageType.sessionStorage)
                return sessionStorage.removeItem(key);
            if (_this.type === StorageType.localStorage)
                return localStorage.removeItem(key);
        };
    }
    return Storage;
}());

var AzureProvider = function (props) {
    var _a = useReducer(reducer, initialState), state = _a[0], dispatch = _a[1];
    var storage = new Storage(props.msalInstance.options.storage);
    var children = props.children;
    var setRedirectInit = function () {
        dispatch({
            type: "INIT_REDIRECT_PAGE_DEFAULT",
            payload: {
                redirectDefault: props.msalInstance.options.redirectDefault,
            },
        });
    };
    var handleRedirect = function () { return __awaiter(void 0, void 0, void 0, function () {
        var resultStorage, result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!props.msalInstance) return [3 /*break*/, 2];
                    dispatch({ type: "INIT_REDIRECT" });
                    resultStorage = storage.getItem(AZURE_AUTHENTICATION_RESULT)
                        ? JSON.parse(storage.getItem(AZURE_AUTHENTICATION_RESULT))
                        : null;
                    return [4 /*yield*/, props.msalInstance.handleRedirect()];
                case 1:
                    result = (_a.sent()) || resultStorage;
                    // setAuthenticationResult(result);
                    dispatch({ type: "END_REDIRECT", payload: result });
                    if (result) {
                        //setIsAuthenticated(true);
                        storage.setItem(AZURE_AUTHENTICATION_RESULT, JSON.stringify(result));
                    }
                    _a.label = 2;
                case 2: return [2 /*return*/];
            }
        });
    }); };
    var refreshToken = function (scopesParam, methodHelper, isSetStorage) { return __awaiter(void 0, void 0, void 0, function () {
        var result, accessToken;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!props.msalInstance) return [3 /*break*/, 2];
                    if (!((_a = state.authenticationResult) === null || _a === void 0 ? void 0 : _a.account)) return [3 /*break*/, 2];
                    dispatch({ type: "INIT_REFRESHTOKEN" });
                    return [4 /*yield*/, props.msalInstance.refreshToken(state.authenticationResult.account, scopesParam)];
                case 1:
                    result = _b.sent();
                    if (methodHelper !== undefined) {
                        accessToken = result.accessToken;
                        methodHelper(accessToken);
                    }
                    if (isSetStorage) {
                        storage.setItem(AZURE_AUTHENTICATION_RESULT, JSON.stringify(result));
                    }
                    dispatch({ type: "END_REFRESHTOKEN", payload: result });
                    _b.label = 2;
                case 2: return [2 /*return*/];
            }
        });
    }); };
    var logout = function () {
        var _a;
        (_a = props.msalInstance) === null || _a === void 0 ? void 0 : _a.logout();
        storage.removeItem(AZURE_AUTHENTICATION_RESULT);
    };
    var login = function (scopesParam) { var _a; return (_a = props.msalInstance) === null || _a === void 0 ? void 0 : _a.login(scopesParam); };
    var checkTokenExist = function () {
        var token = storage.getItem(AZURE_AUTHENTICATION_RESULT);
        if (!token && state.isAuthenticated) {
            dispatch({
                type: "SET_REDIRECT_PAGE_ERROR",
                payload: props.msalInstance.options.redirectError,
            });
        }
    };
    useEffect(function () {
        if (props.msalInstance) {
            setRedirectInit();
            handleRedirect();
        }
    }, [props.msalInstance]);
    useEffect(function () {
        window.addEventListener("storage", checkTokenExist);
        return function () {
            window.removeEventListener("storage", checkTokenExist);
        };
    }, []);
    return (jsx(AzureContext.Provider, __assign({ value: __assign(__assign({ msalInstance: props.msalInstance, refreshToken: refreshToken }, state), { logout: logout,
            login: login }) }, { children: children }), void 0));
};

var AzurePrivateRoute = function (props) {
    var _a = useContext(AzureContext), isAuthenticated = _a.isAuthenticated, redirect = _a.redirect;
    return isAuthenticated ? (jsx(Route, __assign({}, props), void 0)) : (jsx(Redirect, { to: { pathname: redirect } }, void 0));
};

var Msal = /** @class */ (function () {
    function Msal(options) {
        var _this = this;
        this.options = options;
        this.getAccountByUserName = function (username) {
            return _this._msalInstance.getAccountByUsername(username);
        };
        this.login = function (scopesParam) {
            var _a;
            var request = {
                scopes: scopesParam || [],
            };
            return (_a = _this._msalInstance) === null || _a === void 0 ? void 0 : _a.loginRedirect(request);
        };
        this.logout = function () {
            return _this._msalInstance.logout();
        };
        this.handleRedirect = function () {
            return _this._msalInstance.handleRedirectPromise();
        };
        this.refreshToken = function (account, scopesParam) {
            var request = {
                account: account,
                scopes: scopesParam || [],
            };
            return _this._msalInstance.acquireTokenSilent(request);
        };
        var msalConfig = {
            auth: {
                clientId: options.clientId,
                authority: options.authority,
                redirectUri: options.redirectUri,
                postLogoutRedirectUri: options.redirectUri,
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: options.storage,
                storeAuthStateInCookie: false,
            },
            system: {
                loggerOptions: {
                    loggerCallback: function (level, message, containsPii) {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case LogLevel.Error:
                                console.error(message);
                                return;
                            case LogLevel.Info:
                                console.info(message);
                                return;
                            case LogLevel.Verbose:
                                console.debug(message);
                                return;
                            case LogLevel.Warning:
                                console.warn(message);
                                return;
                        }
                    },
                },
            },
        };
        this._msalInstance = new PublicClientApplication(msalConfig);
    }
    return Msal;
}());

/* eslint-disable @typescript-eslint/no-explicit-any */
var accountInfo = {
    homeAccountId: "string",
    environment: "string",
    tenantId: "string",
    username: "string",
    localAccountId: "string",
    name: "username",
};
var authenticationResult = {
    authority: "string",
    uniqueId: "string",
    tenantId: "string",
    scopes: [],
    account: accountInfo,
    idToken: "string",
    idTokenClaims: {},
    accessToken: "string",
    fromCache: true,
    expiresOn: new Date(),
    tokenType: "string",
    extExpiresOn: new Date(),
    state: undefined,
    familyId: undefined,
};
var MsalMock = /** @class */ (function () {
    function MsalMock(options) {
        this.options = options;
    }
    MsalMock.prototype.getAccountByUserName = function () {
        return accountInfo;
    };
    MsalMock.prototype.login = function () {
        return new Promise(function (resolve) {
            resolve(undefined);
        });
    };
    MsalMock.prototype.logout = function () {
        return new Promise(function (resolve) {
            resolve(undefined);
        });
    };
    MsalMock.prototype.handleRedirect = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve) {
                        return resolve(authenticationResult);
                    })];
            });
        });
    };
    MsalMock.prototype.refreshToken = function () {
        return new Promise(function (resolve) {
            resolve(authenticationResult);
        });
    };
    return MsalMock;
}());

export { AzureContext, AzurePrivateRoute, AzureProvider, Msal, MsalMock, StorageType };
